import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../services/api";
import { getMyPurchases } from "../services/purchase";
import { 
  PlayCircle, 
  ArrowRight,
  ArrowLeft,
  Calendar,
  Clock,
  Users,
  Video
} from "lucide-react";
import { motion } from "framer-motion";
import toast, { Toaster } from 'react-hot-toast';
import PublicNavbar from "../components/PublicNavbar";

export default function PublicLiveClasses() {
  const navigate = useNavigate();
  const [liveClasses, setLiveClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasedIds, setPurchasedIds] = useState([]);

  useEffect(() => {
    if (localStorage.getItem("token")) {
      getMyPurchases().then(res => setPurchasedIds(res.data.map(c => String(c.id)))).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const fetchLive = async () => {
      try {
        const res = await api.get("/live/public/list");
        setLiveClasses(res.data);
      } catch (err) {
        console.error("Error fetching live classes:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchLive();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 font-['Inter',sans-serif]">
      <Toaster position="top-center" />
      
      <PublicNavbar />

      <main className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-12 gap-6">
          <div>
            <h2 className="text-3xl font-black text-slate-900 mb-2">Live Interactive Classes</h2>
            <p className="text-slate-500 font-medium">Real-time learning with India's top educators</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {liveClasses.length > 0 ? liveClasses.map((item, i) => (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                key={i} 
                className="bg-white p-8 rounded-[3rem] border border-slate-100 shadow-sm hover:shadow-xl transition-all"
              >
                <div className="flex items-center gap-3 text-red-500 font-black text-xs uppercase tracking-widest mb-8 bg-red-50 w-fit px-4 py-2 rounded-full border border-red-100">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-ping" /> Scheduled
                </div>
                <h3 className="text-2xl font-black text-slate-900 mb-6 leading-tight">Interactive Session for Course ID: {item.course_id}</h3>
                
                <div className="space-y-4 mb-10">
                  <div className="flex items-center gap-3 text-slate-500 font-bold">
                    <Calendar size={20} className="text-indigo-600" />
                    <span>{new Date(item.scheduled_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-500 font-bold">
                    <Clock size={20} className="text-indigo-600" />
                    <span>{new Date(item.scheduled_at).toLocaleTimeString()}</span>
                  </div>
                </div>
                
                {item.is_free && item.youtube_url ? (
                  <button 
                    onClick={() => window.open(item.youtube_url, '_blank')}
                    className="w-full py-4 bg-red-600 text-white font-black rounded-2xl flex items-center justify-center gap-2 hover:bg-red-700 transition-all shadow-lg shadow-red-100"
                  >
                    <PlayCircle size={20} /> Watch Free on YouTube
                  </button>
                ) : (
                  <button 
                    onClick={() => navigate('/login')}
                    className="w-full py-4 bg-indigo-600 text-white font-black rounded-2xl flex items-center justify-center gap-2 hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100"
                  >
                    <Video size={20} /> {purchasedIds.includes(String(item.course_id)) ? "Join Class" : "Enroll Now"}
                  </button>
                )}
              </motion.div>
            )) : (
              <div className="col-span-full py-20 bg-white border-2 border-dashed border-slate-200 rounded-[3rem] text-center text-slate-400 font-bold uppercase tracking-widest italic">
                No live classes scheduled at the moment
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
